import { useState, useEffect, useRef } from "react";
import { Menu } from "lucide-react";
import { MdHome,  MdLogout } from "react-icons/md";
import { SlNote, SlReload } from "react-icons/sl";
import { MdEvent,MdOutlineFactCheck } from "react-icons/md";
import { FaBox, FaHistory } from "react-icons/fa";
import { RiFileExcel2Fill } from "react-icons/ri";
import { MdReorder } from "react-icons/md";
import { MdNewReleases } from "react-icons/md";
import { MdSettings, MdPerson } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { useSession } from "./hooks/useSession";
import { useUser } from "./contexts/UserContext";
import { useOrderEntryModal } from "./contexts/OrderEntryModalContext";
import { clearNewItemPhotoCache } from "./utils/newItemPhotoCache";
import AnnouncementBell from "./components/AnnouncementBell";

export default function Sidebar({ children }) {
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { extendSession, endSession } = useSession();

  const pageTitle = location.pathname.includes('order-entry')
    ? 'Order Entry'
    : location.pathname.includes('order-history')
      ? 'Order History'
      : location.pathname.includes('event-excel-download')
        ? 'Event Excel Download'
        : location.pathname.includes('event-item-edit')
          ? 'Item Order Edit'
          : location.pathname.includes('new-item-application')
            ? 'New Item Application'
          : location.pathname.includes('user-registration')
            ? 'User Registration'
          : location.pathname.includes('announcements')
            ? 'Announcements'
          : 'Supplier Portal';
  const { user } = useUser();
  const mainContentRef = useRef(null);
  const isOrderEntry = location.pathname.includes('order-entry') || location.pathname.includes('event-item-edit');
  const isOrderHistory = location.pathname.includes('order-history');
  const isNewItemApplication = location.pathname.includes('new-item-application');
  const isAdmin = Number(user?.auth_class) === 0;
  const canNewItemApplication = Number(user?.new_item_flg) === 1;
  const { isModalOpen } = useOrderEntryModal() ?? {};

  const handleReloadOrderHistory = () => {
    window.dispatchEvent(new CustomEvent('reload-order-history'));
  };

  const handleReloadNewItemApplication = () => {
    window.dispatchEvent(new CustomEvent('reloadNewItemApplication'));
  };

  // Order Entry時のみ: メインコンテンツのプルリフレッシュ無効化（Chrome: overscroll-behavior / Safari: touchmove）
  // 実際にスクロールしている要素（子孫の overflow-y-auto 等）の scrollTop を参照する
  useEffect(() => {
    if (!isOrderEntry || !mainContentRef.current) return;
    let startY = 0;
    const el = mainContentRef.current;
    const getScrollContainer = (target) => {
      let node = target;
      while (node && node !== document.body) {
        const style = getComputedStyle(node);
        const oy = style.overflowY;
        if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };
    const handleTouchStart = (e) => {
      startY = e.touches[0]?.pageY ?? 0;
    };
    const handleTouchMove = (e) => {
      const scrollEl = getScrollContainer(e.target);
      // スクロールコンテナが見つからない場合は prevent しない（明細スクロールを妨げない）
      if (!scrollEl) return;
      const scrollTop = scrollEl.scrollTop;
      const currentY = e.touches[0]?.pageY ?? 0;
      if (scrollTop <= 0 && startY <= currentY) {
        e.preventDefault();
      }
    };
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOrderEntry]);

  // Order Entryのモーダル表示時: document全体でプルリフレッシュ無効化（Chrome + Safari）
  useEffect(() => {
    if (!isOrderEntry || !isModalOpen) return;
    let startY = 0;
    const getScrollTop = (target) => {
      let el = target;
      while (el && el !== document.body) {
        const style = getComputedStyle(el);
        const oy = style.overflowY;
        if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
          return el.scrollTop;
        }
        el = el.parentElement;
      }
      return document.documentElement.scrollTop || document.body.scrollTop || 0;
    };
    const handleTouchStart = (e) => {
      startY = e.touches[0]?.pageY ?? 0;
    };
    const handleTouchMove = (e) => {
      const scrollTop = getScrollTop(e.target);
      const currentY = e.touches[0]?.pageY ?? 0;
      if (scrollTop <= 0 && startY <= currentY) {
        e.preventDefault();
      }
    };
    document.body.style.overscrollBehavior = 'none';
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      document.body.style.overscrollBehavior = '';
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOrderEntry, isModalOpen]);

  // ユーザー操作でセッション延長
  useEffect(() => {
    const handleActivity = () => extendSession();
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, [extendSession]);

  const handleOutsideClick = (e) => {
    if (
      e.target.closest('aside') === null &&
      !e.target.closest('button.menu-trigger')
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    clearNewItemPhotoCache();
    localStorage.clear();
    endSession();
    window.location.href = '/login';
  };

  // サイドバー内のUser表示
  const UserMenuContent = () => (
    <div className="flex flex-row items-start text-left text-gray-800 min-w-0">
      <span className="text-base font-bold truncate max-w-full text-gray-600">User : </span>
      <span className="ml-1 text-base font-bold truncate max-w-full text-blue-800">{user?.userName || 'Unknown'}</span>
    </div>
  );

  return (
    <div className="flex h-dvh bg-gray-100 overflow-y-hidden">
      <main className="flex flex-col flex-1 min-h-0 w-full md:ml-0">
        <aside
          className={`fixed inset-y-0 left-0 w-[280px] bg-white text-gray-800 p-0 shadow-lg transform transition-transform duration-300 ease-in-out z-[70] flex flex-col
            ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
          onMouseLeave={() => {
            if (window.innerWidth >= 768) setIsOpen(false);
          }}
        >
          <div className="mb-0 flex-shrink-0">
            <h2 className="w-full text-xl font-semibold text-white text-left bg-blue-500 pl-3">KOBE-YA Supplier Portal</h2>
          </div>

          <div className="mb-0 px-4 py-2 bg-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                className="p-2 rounded-full hover:bg-gray-300 transition-colors"
                onClick={() => { navigate('/dashboard'); setIsOpen(false); }}
                title="Supplier Portal"
              >
                <MdHome className="w-6 h-6 text-gray-700" />
              </button>
              <div className="flex-1 min-w-0">
                <UserMenuContent />
              </div>
              <button
                className="p-2 rounded-full hover:bg-gray-300 transition-colors flex-shrink-0"
                onClick={handleLogout}
                title="Logout"
              >
                <MdLogout className="w-6 h-6 text-gray-700" />
              </button>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto">
            <ul className="space-y-0.5 px-0 py-0">
              <li className="w-full flex items-center gap-2 text-left px-1 py-2 border-b bg-gray-100 border-gray-200 text-gray-800 hover:bg-gray-100 rounded transition-colors">
                  <MdEvent className="w-5 h-5 text-green-700 flex-shrink-0" />
                  <span className="font-bold text-green-700 text-base">Event System</span>
              </li>
              <li>
                <button
                  onClick={() => { navigate('/dashboard/order-entry'); setIsOpen(false); }}
                  className="w-full flex items-center gap-2 text-left px-3 py-2 ml-6 text-gray-800 hover:bg-gray-100 rounded transition-colors"
                >
                  <SlNote className="w-5 h-5 text-blue-700 flex-shrink-0" />
                  <span className="font-bold text-base">Order Entry</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => { navigate('/dashboard/order-history'); setIsOpen(false); }}
                  className="w-full flex items-center gap-2 text-left px-3 py-2 ml-6 text-gray-800 hover:bg-gray-100 rounded transition-colors"
                >
                  <FaHistory className="w-5 h-5 text-red-700 flex-shrink-0" />
                  <span className="font-bold text-base">Order History</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => { navigate('/dashboard/event-excel-download'); setIsOpen(false); }}
                  className="w-full flex items-center gap-2 text-left px-3 py-2 ml-6 text-gray-800 hover:bg-gray-100 rounded transition-colors"
                >
                  <RiFileExcel2Fill className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="font-bold text-base">Event Excel Download</span>
                </button>
              </li>
              {isAdmin && (
                <li>
                  <button
                    onClick={() => { navigate('/dashboard/event-item-edit'); setIsOpen(false); }}
                    className="w-full flex items-center gap-2 text-left px-3 py-2 ml-6 text-gray-800 hover:bg-gray-100 rounded transition-colors"
                  >
                    <MdReorder className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <span className="font-bold text-base">Item Order Edit</span>
                  </button>
                </li>
              )}

              {canNewItemApplication && (
                <>
                  <li className="w-full flex items-center gap-2 text-left px-1 py-2 border-b bg-gray-100 border-gray-200 text-gray-800 hover:bg-gray-100 rounded transition-colors mt-3">
                      <MdOutlineFactCheck className="w-5 h-5 text-sky-700 flex-shrink-0" />
                      <span className="font-bold text-sky-700 text-base">Application</span>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/dashboard/new-item-application');
                      }}
                      className="w-full flex items-center gap-2 text-left px-3 py-2 ml-6 text-gray-800 hover:bg-gray-100 rounded transition-colors"
                    >
                      <FaBox className="w-5 h-5 text-sky-700 flex-shrink-0" />
                      <span className="font-bold text-base">New Item Application</span>
                    </button>
                  </li>
                </>
              )}

              {isAdmin && (
                <>
                  <li className="w-full flex items-center gap-2 text-left px-1 py-2 border-b bg-gray-100 border-gray-200 text-gray-800 hover:bg-gray-100 rounded transition-colors mt-3">
                    <MdSettings className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    <span className="font-bold text-gray-600 text-base">Master</span>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/dashboard/user-registration');
                      }}
                      className="w-full flex items-center gap-2 text-left px-3 py-2 ml-6 text-gray-800 hover:bg-gray-100 rounded transition-colors"
                    >
                      <MdPerson className="w-5 h-5 text-gray-800 flex-shrink-0" />
                      <span className="font-bold text-base">User Registration</span>
                    </button>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </aside>

        <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white shadow-sm z-40">
          <div className="flex items-center justify-between px-4 h-full">
            <div className="flex items-center">
              <button
                className="menu-trigger p-2 rounded-full hover:bg-gray-100 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(!isOpen);
                }}
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
              <h1 className="text-xl font-semibold ml-4 text-gray-800">{pageTitle}</h1>
            </div>
            <div className="flex items-center gap-1">
              <AnnouncementBell />
              {isNewItemApplication && (
                <button
                  type="button"
                  className="p-1 rounded-full hover:bg-white transition-colors"
                  onClick={handleReloadNewItemApplication}
                  title="Reload"
                >
                  <SlReload className="w-5 h-5 text-gray-700" />
                </button>
              )}
              {user?.maker_short_name && (
                <span className="text-sm font-semibold text-gray-700">{user.maker_short_name}</span>
              )}
              {isOrderHistory && (
                <button
                  className="p-1 rounded-full hover:bg-white transition-colors"
                  onClick={handleReloadOrderHistory}
                  title="Reload"
                >
                  <SlReload className="w-5 h-5 text-gray-700" />
                </button>
              )}
            </div>
          </div>
        </header>

        <header className="hidden md:flex flex-shrink-0 h-12 border-b border-gray-100 bg-white shadow-sm items-center pl-0 pr-4">
          <div className="flex-1 flex items-center gap-2">
            <button
              className="menu-trigger p-2 bg-blue-400 hover:bg-blue-500 rounded-sm transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              onMouseEnter={() => setIsOpen(true)}
            >
              <Menu className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800 m-0">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-1">
            <AnnouncementBell />
            {isNewItemApplication && (
              <button
                type="button"
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                onClick={handleReloadNewItemApplication}
                title="Reload"
              >
                <SlReload className="w-5 h-5 text-gray-700" />
              </button>
            )}
            {isOrderHistory && (
              <button
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                onClick={handleReloadOrderHistory}
                title="Reload"
              >
                <SlReload className="w-5 h-5 text-gray-700" />
              </button>
            )}
            {user?.maker_short_name && (
              <span className="text-sm font-semibold text-gray-700">{user.maker_short_name}</span>
            )}
          </div>
        </header>

        {isOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[55] md:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}

        <div
          className="hidden md:flex lg:hidden fixed top-1 left-0 z-0 items-center gap-0"
          onMouseEnter={() => setIsOpen(true)}
        >
          <button className="menu-trigger p-2 min-h-full bg-blue-400 hover:bg-blue-500 rounded-sm transition-colors">
            <Menu className="w-6 h-6 text-white" />
          </button>
        </div>

        <div className="flex-1 min-h-0 w-full pt-14 md:pt-0 flex flex-col">
          <div
            ref={mainContentRef}
            className={`w-full flex-1 min-h-0 max-w-none mx-auto bg-white rounded-lg shadow-sm p-0 md:p-0 flex flex-col ${isOrderEntry ? 'overflow-hidden overscroll-none' : 'overflow-auto'}`}
          >
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
